import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Bell, Clock, AlertTriangle, Share, PlusSquare, Smartphone, X } from "lucide-react";
import { toast } from "sonner";
import usePushNotifications from "@/hooks/usePushNotifications";

// IANA timezone options (properly handles daylight saving)
const TIMEZONE_OPTIONS = [
  { value: "Africa/Johannesburg", label: "South Africa (SAST)" },
  { value: "Africa/Lagos", label: "West Africa (WAT)" },
  { value: "Africa/Nairobi", label: "East Africa (EAT)" },
  { value: "Africa/Cairo", label: "Egypt (EET)" },
  { value: "Europe/London", label: "UK (GMT/BST)" },
  { value: "Europe/Paris", label: "Central Europe (CET/CEST)" },
  { value: "Europe/Athens", label: "Eastern Europe (EET/EEST)" },
  { value: "America/New_York", label: "US Eastern (EST/EDT)" },
  { value: "America/Chicago", label: "US Central (CST/CDT)" },
  { value: "America/Denver", label: "US Mountain (MST/MDT)" },
  { value: "America/Los_Angeles", label: "US Pacific (PST/PDT)" },
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Asia/Hong_Kong", label: "Hong Kong (HKT)" },
  { value: "Asia/Tokyo", label: "Japan (JST)" },
  { value: "Australia/Sydney", label: "Australia Eastern (AEST/AEDT)" },
  { value: "Pacific/Auckland", label: "New Zealand (NZST/NZDT)" },
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
];

// Time options (every hour)
const TIME_OPTIONS = [];
for (let h = 5; h <= 22; h++) {
  const hour = h.toString().padStart(2, "0");
  const label = h < 12 ? `${h}:00 AM` : h === 12 ? `12:00 PM` : `${h - 12}:00 PM`;
  TIME_OPTIONS.push({ value: `${hour}:00`, label });
}

export function NotificationSettings() {
  const [settings, setSettings] = useState({
    enabled: false,
    reminder_time: "08:00",
    timezone: "Africa/Johannesburg", // Default to South Africa (IANA name)
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  
  const { 
    isSupported, 
    permission, 
    subscribe, 
    unsubscribe,
    isSubscribed 
  } = usePushNotifications();

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  
  // Detect if running as installed PWA (standalone)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  // Load current settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await axios.get(`${API}/push/status`, { withCredentials: true });
        if (response.data.settings) {
          setSettings({
            enabled: response.data.subscribed && response.data.settings.enabled !== false,
            reminder_time: response.data.settings.reminder_time || "08:00",
            timezone: response.data.settings.timezone || "+2",
          });
        }
      } catch (error) {
        console.error("Failed to load notification settings:", error);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  // Try to detect user's IANA timezone on first load
  useEffect(() => {
    if (!loading && settings.timezone === "Africa/Johannesburg") {
      try {
        // Get the browser's IANA timezone
        const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        // Check if we have a matching option
        const match = TIMEZONE_OPTIONS.find(tz => tz.value === detectedTz);
        if (match) {
          setSettings(prev => ({ ...prev, timezone: match.value }));
        }
      } catch {
        // Keep default
      }
    }
  }, [loading]);

  const handleToggleReminders = async (enabled) => {
    // If enabling on iOS and not standalone, show help
    if (enabled && isIOS && !isStandalone) {
      setShowIOSHelp(true);
      return;
    }

    // If enabling, request permission first
    if (enabled) {
      if (!isSupported) {
        toast.error("Push notifications are not supported in this browser");
        return;
      }

      if (permission === "denied") {
        toast.error("Notifications are blocked. Please enable them in your browser settings.");
        return;
      }

      setSaving(true);
      try {
        // Request permission and subscribe
        const result = await subscribe();
        if (!result) {
          toast.error("Could not enable notifications. Please check your browser settings.");
          setSaving(false);
          return;
        }
        
        // Save settings to backend
        await axios.put(`${API}/push/settings`, {
          enabled: true,
          reminder_time: settings.reminder_time,
          timezone: settings.timezone
        }, { withCredentials: true });
        
        setSettings(prev => ({ ...prev, enabled: true }));
        toast.success("Daily reminders enabled!");
      } catch (error) {
        console.error("Failed to enable notifications:", error);
        toast.error("Failed to enable notifications");
      } finally {
        setSaving(false);
      }
    } else {
      // Disabling
      setSaving(true);
      try {
        await unsubscribe();
        await axios.put(`${API}/push/settings`, {
          enabled: false,
          reminder_time: settings.reminder_time,
          timezone: settings.timezone
        }, { withCredentials: true });
        
        setSettings(prev => ({ ...prev, enabled: false }));
        toast.success("Daily reminders disabled");
      } catch (error) {
        console.error("Failed to disable notifications:", error);
        toast.error("Failed to disable notifications");
      } finally {
        setSaving(false);
      }
    }
  };

  const handleTimeChange = async (value) => {
    setSettings(prev => ({ ...prev, reminder_time: value }));
    
    if (settings.enabled) {
      try {
        await axios.put(`${API}/push/settings`, {
          enabled: true,
          reminder_time: value,
          timezone: settings.timezone
        }, { withCredentials: true });
      } catch (error) {
        console.error("Failed to update reminder time:", error);
      }
    }
  };

  const handleTimezoneChange = async (value) => {
    setSettings(prev => ({ ...prev, timezone: value }));
    
    if (settings.enabled) {
      try {
        await axios.put(`${API}/push/settings`, {
          enabled: true,
          reminder_time: settings.reminder_time,
          timezone: value
        }, { withCredentials: true });
      } catch (error) {
        console.error("Failed to update timezone:", error);
      }
    }
  };

  const handleTestNotification = async () => {
    try {
      await axios.post(`${API}/push/test`, {}, { withCredentials: true });
      toast.success("Test notification sent!");
    } catch (error) {
      console.error("Failed to send test notification:", error);
      toast.error(error.response?.data?.detail || "Failed to send test notification");
    }
  };

  // iOS Help Modal
  if (showIOSHelp) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
        <Card className="glass-card max-w-sm w-full">
          <CardContent className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#a69373]/20 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-[#a69373]" />
                </div>
                <h3 className="text-white font-semibold">Install Compass First</h3>
              </div>
              <button onClick={() => setShowIOSHelp(false)} className="text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-white/70 text-sm mb-4">
              On iPhone, push notifications only work when Compass is installed to your home screen.
            </p>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#a69373]/15 flex items-center justify-center shrink-0">
                  <Share className="w-4 h-4 text-[#a69373]" />
                </div>
                <p className="text-white text-sm">Tap the <strong>Share</strong> button in Safari</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#a69373]/15 flex items-center justify-center shrink-0">
                  <PlusSquare className="w-4 h-4 text-[#a69373]" />
                </div>
                <p className="text-white text-sm">Select <strong>Add to Home Screen</strong></p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#a69373]/15 flex items-center justify-center shrink-0">
                  <Bell className="w-4 h-4 text-[#a69373]" />
                </div>
                <p className="text-white text-sm">Open Compass from your home screen, then enable reminders</p>
              </div>
            </div>
            
            <Button 
              onClick={() => setShowIOSHelp(false)}
              className="w-full bg-[#a69373] hover:bg-[#8b7a5e] text-white"
            >
              Got it
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <Card className="glass-card">
        <CardContent className="p-4">
          <div className="animate-pulse flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-white/10"></div>
            <div className="flex-1">
              <div className="h-4 bg-white/10 rounded w-32 mb-2"></div>
              <div className="h-3 bg-white/10 rounded w-48"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Check if notifications are blocked
  const isBlocked = permission === "denied";

  return (
    <Card className="glass-card overflow-hidden">
      <CardContent className="p-4">
        {/* Main Toggle */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#d8c8a8]/20 flex items-center justify-center">
              <Bell className="w-5 h-5 text-[#d8c8a8]" />
            </div>
            <div>
              <Label htmlFor="reminder-toggle" className="text-white font-medium cursor-pointer">
                Daily Reminders
              </Label>
              <p className="text-white/50 text-sm">
                {isBlocked ? "Blocked in browser settings" : "Get reminded to check in"}
              </p>
            </div>
          </div>
          <Switch
            id="reminder-toggle"
            checked={settings.enabled}
            onCheckedChange={handleToggleReminders}
            disabled={saving || isBlocked}
            data-testid="reminder-toggle"
          />
        </div>

        {/* Blocked Warning */}
        {isBlocked && (
          <div className="p-3 rounded-xl bg-[#a69373]/10 border border-[#a69373]/30 mb-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-[#a69373] mt-0.5 shrink-0" />
              <p className="text-[#a69373] text-sm">
                Notifications are blocked. To enable them, go to your browser's site settings and allow notifications for Compass.
              </p>
            </div>
          </div>
        )}

        {/* Settings (only show when enabled) */}
        {settings.enabled && !isBlocked && (
          <div className="space-y-4 pt-4 border-t border-white/10">
            {/* Reminder Time */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-white/50" />
                <Label className="text-white/70 text-sm">Reminder Time</Label>
              </div>
              <select
                value={settings.reminder_time}
                onChange={(e) => handleTimeChange(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#a69373]"
                data-testid="reminder-time-select"
              >
                {TIME_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value} className="bg-[#0f3129] text-white">
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Timezone */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-white/50" />
                <Label className="text-white/70 text-sm">Your Timezone</Label>
              </div>
              <select
                value={settings.timezone}
                onChange={(e) => handleTimezoneChange(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#a69373] max-w-[180px]"
                data-testid="timezone-select"
              >
                {TIMEZONE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value} className="bg-[#0f3129] text-white">
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Test Button */}
            <Button
              onClick={handleTestNotification}
              variant="outline"
              size="sm"
              className="w-full border-white/20 text-white hover:bg-white/10"
              data-testid="test-notification-btn"
            >
              Send Test Notification
            </Button>

            <p className="text-white/40 text-xs text-center">
              You'll only receive a reminder if you haven't checked in that day.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default NotificationSettings;
